import React, { Fragment, useState, useRef, useEffect, } from 'react';
import { Button, Modal, ModalBody, } from 'reactstrap';
import { isEmpty, invoke, sortBy, last, get, pick, omit, uniqBy, keyBy, } from 'lodash';
import { format as formatDate, startOfDay, endOfDay, differenceInMilliseconds, addSeconds, } from 'date-fns';
import { toast, } from 'react-toastify';
import { useToggle, useDebounce, } from 'react-use';
import classnames from 'classnames';

import firebase from '../../firebase';
import { fullPathWithParams, withConfirm } from '../../util';
import { fieldsToStart } from '../../shared/models/picking';
import { statuses, pendingFields, packageTypes, } from '../../shared/models/pickingItem';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsSubscription from '../hooks/useDocumentsSubscription';
import useQueryParams from '../hooks/useQueryParams';
import StaffPage from '../hocs/StaffPage';
import ModelFormModal from '../modals/ModelFormModal';
import DateSelector from '../DateSelector';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import QueryInput from '../QueryInput';
import QuerySelector from '../QuerySelector';
import BatchImportButton from '../BatchImportButton';

const { keys, entries } = Object;
const auth = firebase.auth();
const db = firebase.firestore();
const usersRef = db.collection('users');
const pickingsRef = db.collection('pickings');
const waitingDuration = 90;
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));

export default StaffPage(function StaffPicking (props) {
  const { user, history, location, } = props;
  const { pickingItemQuery, date: _date, statuses: statusesForFilter = [], } = useQueryParams();
  const [startId, setStartId] = useState();
  const date = _date && new Date(_date);
  const audioEl = useRef(null);
  const [hasInitialPlayed, toggleInitialPlayed] = useToggle(false);
  const users = useCollectionSubscription(usersRef.where('isStaff', '==', true));
  const usersById = keyBy(users, 'id');
  const pickings = useCollectionSubscription(pickingsRef.where('userId', '==', user.id));
  let pickingItemsRef = db.collection('pickingItems');
  if(!isEmpty(statusesForFilter)) {
    pickingItemsRef = pickingItemsRef.where('status', 'in', statusesForFilter);
  }
  const pickingItems = useCollectionSubscription(_date && pickingItemsRef.where('createdAt', '>=', startOfDay(date)).where('createdAt', '<=', endOfDay(date)), [_date, statusesForFilter.join('')]);
  const initialPicking = pickings.find(_ => _.status === 'initial');
  const selfPickingItems = useCollectionSubscription(pickingItemsRef.where('pickingBy', '==', user.id).where('status', '==', 'picking'));
  const sortedItems = sortBy(pickingItems, _ => _.index);
  
  let filteredItems = sortedItems;
  if(pickingItemQuery) {
    filteredItems = filteredItems.filter(_ => _.id.match(pickingItemQuery));
  }

  const lastUpdatedItem = last(sortBy(filteredItems.filter(_ => _.updatedBy === user.id), _ => _.updatedAt?.toDate() || new Date(1900, 0, 1)));
  const lastUpdatedItemIndex = filteredItems.indexOf(lastUpdatedItem?.updatedAt ? lastUpdatedItem : filteredItems.find(_ => _.pickingBy === user.id && _.status === 'picking'));
  const displayItems = filteredItems.slice(Math.max(0, lastUpdatedItemIndex - 5), lastUpdatedItemIndex + 6);

  const isPicking = initialPicking != null;
  const hasPickingItem = selfPickingItems.length > 0;
  const onStart = async (values) => {
    if(!hasInitialPlayed) {
      audioEl.current.play();
      audioEl.current.pause();
      toggleInitialPlayed(true);
    }
    const pickingItemsRef = db.collection('pickingItems');
    const pickingItemDoc = await pickingItemsRef.doc(values.startId).get();
    if(!pickingItemDoc.exists) return toast.error('存在しないIDです');
    
    const pickingItem = { ...pick(pickingItemDoc, ['id', 'ref']), ...pickingItemDoc.data() };
    if(pickingItem.status === 'picked') return toast.error('すでに作業済みです');

    await pickingsRef.add({
      ...values,
      createdAt: new Date(),
      status: 'initial',
      userId: user.id,
      startedAt: new Date(),
      activities: pickingItem.status === 'initial' ? [{
        pickingItemId: values.startId,
        packageType: pickingItem.packageType,
        startedAt: new Date(),
      }] : [],
    });
    if(pickingItem.status === 'initial') {
      await pickingItem.ref.update({ status: 'picking', pickingBy: user.id, updatedBy: user.id, updatedAt: new Date(), });
    }

    const path = fullPathWithParams({ date: formatDate(pickingItem.createdAt.toDate(), 'yyyy-MM-dd') }, location);
    history.replace(encodeURI(path));
    setStartId(values.startId);
  };
  const onClickFinish = async () => {
    if(!window.confirm('本当に終了しますか？')) return;

    await initialPicking.ref.update({ status: 'finished', finishedAt: new Date(), updatedBy: user.id, updatedAt: new Date(), });
  };
  const onClickRestart = async () => {
    audioEl.current.play();
    audioEl.current.pause();
    toggleInitialPlayed(true);
  };
  const onClickLogOut = async () => {
    if(!window.confirm('ログアウトしますか？')) return;
    await auth.signOut();
    window.location.reload();
  };
  useEffect(() => {
    if(!hasInitialPlayed || initialPicking == null) return;

    const lastActivity = last(initialPicking.activities);
    if(lastActivity == null) return;

    const timeoutId = setTimeout(_ => {
      audioEl.current.play();
    }, differenceInMilliseconds(addSeconds(lastActivity.startedAt.toDate(), waitingDuration), new Date()));

    return () => {
      audioEl.current.pause();
      clearTimeout(timeoutId);
    };
  }, [initialPicking, hasInitialPlayed]);
  useDebounce(() => {
    const elm = document.querySelector(`[data-id="${startId}"]`);
    if(elm == null) return;

    elm.scrollIntoView();
    setStartId(null);
  }, 500, [pickingItems.length, startId]);

  return (
    <div className="settings-senders container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>ピッキング</h4>
        </div>
        <div className="d-flex justify-content-start mb-3">
          <div className="d-flex align-items-end flex-wrap">
            <DateSelector paramName="date" label="インポート日" defaultValue={new Date()} history={history} location={location} />
            <QuerySelector className="ml-2" paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" />
          </div>
        </div>
        <div className="d-flex justify-content-start mb-3">
        </div>
        <div className="mt-4 overflow-scroll position-relative" style={{ zIndex: 0 }}>
          {
            filteredItems.length > 0 ? (
              <table className="table table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th></th>
                    <th style={{ minWidth: 150 }}>ID</th>
                    <th style={{ minWidth: 150 }}>商品名</th>
                    <th style={{ minWidth: 100 }}>梱包種別</th>
                    <th style={{ minWidth: 100 }}>オプション1</th>
                    <th style={{ minWidth: 100 }}>オプション2</th>
                    <th style={{ minWidth: 100 }}>状態</th>
                    <th style={{ minWidth: 150 }}>作業者</th>
                    <th style={{ minWidth: 150 }}>保留メモ</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    displayItems.map((pickingItem) => {
                      const { id, ref, productName, packageType, free1, free2, status = 'initial', pickingBy, pendingNote, } = pickingItem;
                      const pickingUser = usersById[pickingBy];
                      const { color } = statuses[status];
                      const onClickWaitMulti = async () => {
                        await ref.update({
                          status: 'waitingToMulti',
                          pendedBy: user.id,
                          pendingNote: `複数 ${user.displayName}`,
                          updatedBy: user.id,
                          updatedAt: new Date(),
                        });
                      };
                      const onClickUnstartItem = async () => {
                        const batch = db.batch();
                        batch.set(initialPicking.ref, {
                          activities: initialPicking.activities.filter(_ => _.pickingItemId !== id),
                        }, { merge: true });
                        batch.update(ref, { status: 'initial', pickingBy: null, pickedBy: null, pickedAt: null, updatedBy: user.id, updatedAt: new Date(), });
                        await batch.commit();
                      };
                      const onClickStartItem = async () => {
                        const batch = db.batch();
                        batch.set(initialPicking.ref, {
                          activities: firebase.firestore.FieldValue.arrayUnion({
                            pickingItemId: id,
                            packageType,
                            startedAt: new Date(),
                          }),
                        }, { merge: true });
                        batch.update(ref, { status: 'picking', pickingBy: user.id, updatedBy: user.id, updatedAt: new Date(), });
                        await batch.commit();
                      };

                      return (
                        <tr key={id} className={`table-${color}`} data-id={id}>
                          <td className="text-nowrap">
                            {
                              status === 'initial' && (
                                <Button color="primary" onClick={onClickStartItem} disabled={hasPickingItem}>
                                  着手する
                                </Button>
                              )
                            }
                            {
                              ((status === 'picking' && pickingItem.pickingBy === user.id) || status === 'pending' || status === 'waitingToMulti') && (
                                <Button color="success" onClick={_ => ref.update({ status: 'picked', pickingBy: user.id, pickedBy: user.id, pickedAt: new Date(), updatedBy: user.id, updatedAt: new Date(), })}>
                                  <span className="fas fa-check mr-1" />
                                  チェック済
                                </Button>
                              )
                            }
                            {
                              status === 'picking' && pickingItem.pickingBy === user.id && (
                                <PendingButton className="ml-2" itemRef={ref} user={user} />
                              )
                            }
                            {
                              status === 'picking' && pickingItem.pickingBy === user.id && (
                                <Button className="ml-2" color="secondary" onClick={onClickWaitMulti}>
                                  複数待ち
                                </Button>
                              )
                            }
                            {
                              status !== 'initial' && (
                                <Button className="ml-2" color="warning" onClick={onClickUnstartItem}>
                                  未着手に戻す
                                </Button>
                              )
                            }
                          </td>
                          <td>{id}</td>
                          <td>{productName}</td>
                          <td>{get(packageTypes, [packageType, 'label'], '')}</td>
                          <td>{free1}</td>
                          <td>{free2}</td>
                          <td>{statuses[status].label}</td>
                          <td>{pickingUser && pickingUser.displayName}</td>
                          <td>
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {pendingNote}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                ピッキングアイテムはありません
              </div>
            )
          }
        </div>
      </div>
      {
        !isPicking && (
          <ModelFormModal
            headerClassName="d-block"
            title={
              <div className="w-100 d-flex align-items-center justify-content-between">
                <div>ピッキングを開始する</div>
                <Button onClick={onClickLogOut}>ログアウト</Button>
              </div>
            }
            isOpen
            fields={fieldsToStart}
            submitLabel="開始"
            onSubmit={onStart}
          />
        )
      }
      {
        isPicking && !hasInitialPlayed && (
          <Modal isOpen>
            <ModalBody className="d-flex align-items-center justify-content-center" style={{ minHeight: 200, }}>
              <Button onClick={onClickRestart}>
                再開する
              </Button>
            </ModalBody>
          </Modal>
        )
      }
      <div className="flex-fill d-flex position-fixed align-items-end" style={{ zIndex: 10, bottom: 10, right: 10, }}>
        <div style={{ width: 300 }} >
          <QueryInput width={null} paramName="pickingItemQuery" label="IDで検索" />
        </div>
        <Button color="primary" className="ml-2" onClick={onClickFinish} disabled={hasPickingItem}>
          終了する
        </Button>
      </div>
      <audio src="https://soundbible.com//mp3/sms-alert-5-daniel_simon.mp3" ref={audioEl} loop />
    </div>
  );
});

function PendingButton(props) {
  const { itemRef: ref, user, ...extraProps } = props;
  const [showsModal, toggleModal] = useToggle(false);
  const onSubmit = async (values) => {
    await ref.update({ status: 'pending', pendedBy: user.id, ...values, updatedBy: user.id, updatedAt: new Date(), });
    toggleModal(false);
  };

  return (
    <Fragment>
      <Button color="secondary" onClick={toggleModal.bind(null, true)} {...extraProps}>
        保留
      </Button>
      {showsModal && <ModelFormModal isOpen title="保留" fields={pendingFields} onClickClose={toggleModal.bind(null, false)} onSubmit={onSubmit} />}
    </Fragment>
  );
};
